import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FACTOR_DATA } from '../data/factorInterpretations'; // ✅ CORRECT: Import from factorInterpretations.js
import { getAnswerLabel } from '../utils/answerFormatHelper'; // ✅ NEW: Import answer helper
import LoadingSpinner from '../components/LoadingSpinner';
import TeachingPresentation from '../components/TeachingPresentation'; // ✅ NEW
import PrayerPresentation from '../components/PrayerPresentation'; // ✅ NEW
import { BookOpen, Heart, ArrowLeft } from 'lucide-react';

const FactorResultsPage = () => {
  const { factorNumber } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [canView, setCanView] = useState(false);
  const [factorData, setFactorData] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [userScore, setUserScore] = useState(0);
  const [partnerScore, setPartnerScore] = useState(0);
  const [partnerName, setPartnerName] = useState('');
  
  // ✅ NEW: State for presentation modals
  const [showTeaching, setShowTeaching] = useState(false);
  const [showPrayer, setShowPrayer] = useState(false);

  useEffect(() => {
    loadFactorResults();
  }, [factorNumber, currentUser]);

  const loadFactorResults = async () => {
    try {
      setLoading(true);
      setError('');

      const factorNum = parseInt(factorNumber);

      // Get user data
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        setError('User data not found');
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      const coupleId = userData.coupleId;
      const partnerId = userData.partnerId;

      if (!coupleId) {
        setError('Not part of a couple');
        setLoading(false);
        return;
      }

      // Get couple data
      const coupleDoc = await getDoc(doc(db, 'couples', coupleId));
      if (!coupleDoc.exists()) {
        setError('Couple data not found');
        setLoading(false);
        return;
      }

      const coupleData = coupleDoc.data();
      const factorPermissions = coupleData.factorPermissions || {};
      const permission = factorPermissions[factorNum];

      // Check if results are revealed
      if (!permission?.resultsRevealed) {
        setCanView(false);
        setError('Results not yet revealed by your counselor');
        setLoading(false);
        return;
      }

      setCanView(true);

      // Get user's assessment
      const userAssessment = await getDoc(doc(db, 'assessments', currentUser.uid));
      if (userAssessment.exists()) {
        const assessmentData = userAssessment.data();
        setUserAnswers(assessmentData.answers || {});
        setUserScore(assessmentData.factorScores?.[factorNum] || 0);
      }

      // Get partner's assessment
      if (partnerId) {
        const partnerDoc = await getDoc(doc(db, 'users', partnerId));
        if (partnerDoc.exists()) {
          setPartnerName(partnerDoc.data().name || 'Your Partner');
        }

        const partnerAssessment = await getDoc(doc(db, 'assessments', partnerId));
        if (partnerAssessment.exists()) {
          const partnerData = partnerAssessment.data();
          setPartnerScore(partnerData.factorScores?.[factorNum] || 0);
        }
      }

      // ✅ FIXED: Load factor data from FACTOR_DATA
      const factor = FACTOR_DATA[factorNum];
      
      if (factor) {
        setFactorData(factor);
      } else {
        setError('Factor data not found');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading factor results:', err);
      setError('Error loading results: ' + err.message);
      setLoading(false);
    }
  };

  const getInterpretation = (score) => {
    if (!factorData?.scoringGuidance) return null;

    const { excellent, developing, vulnerable } = factorData.scoringGuidance;

    if (score >= excellent.range[0] && score <= excellent.range[1]) {
      return { ...excellent, color: 'green' };
    } else if (score >= developing.range[0] && score <= developing.range[1]) {
      return { ...developing, color: 'yellow' };
    } else if (score >= vulnerable.range[0] && score <= vulnerable.range[1]) {
      return { ...vulnerable, color: 'red' };
    }

    return null;
  };

  // ✅ UPDATED: Use helper function for answer text
  const getAnswerText = (question, answerValue) => {
    return getAnswerLabel(answerValue, question);
  };

  if (loading) {
    return <LoadingSpinner message="Loading results..." />;
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Results Not Available</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/couple/dashboard')}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error || !factorData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Factor data not found'}</p>
          <button
            onClick={() => navigate('/couple/dashboard')}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const interpretation = getInterpretation(userScore);
  const interpretationColor = interpretation?.color === 'green' 
    ? 'bg-green-50 border-green-200 text-green-800'
    : interpretation?.color === 'yellow'
    ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
    : 'bg-red-50 border-red-200 text-red-800';

  return (
    <>
      {/* ✅ NEW: Teaching Presentation Modal */}
      {showTeaching && (
        <TeachingPresentation
          factorNumber={parseInt(factorNumber)}
          onClose={() => setShowTeaching(false)}
        />
      )}

      {/* ✅ NEW: Prayer Presentation Modal */}
      {showPrayer && (
        <PrayerPresentation
          factorNumber={parseInt(factorNumber)}
          onClose={() => setShowPrayer(false)}
        />
      )}

      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
        <div className="max-w-4xl mx-auto py-8">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <button
              onClick={() => navigate('/couple/dashboard')}
              className="text-blue-600 hover:text-blue-700 font-medium mb-4 flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>

            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-bold text-gray-900">
                Factor {factorNumber} Results
              </h1>
              <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Revealed by Counselor
              </span>
            </div>

            <h2 className="text-xl text-gray-700 font-medium">{factorData.name}</h2>
          </div>

          {/* ✅ NEW: Review Content Buttons */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
              📖 Review Teaching & Prayer Content
            </h2>
            <p className="text-gray-600 mb-6">
              Revisit the teaching content and prayer points for this factor to deepen your understanding and spiritual growth.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={() => setShowTeaching(true)}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg"
              >
                <BookOpen className="w-6 h-6" />
                <div className="text-left">
                  <div className="font-semibold text-lg">Review Teaching</div>
                  <div className="text-sm opacity-90">8 teaching slides</div>
                </div>
              </button>

              <button
                onClick={() => setShowPrayer(true)}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-xl transition-all shadow-md hover:shadow-lg"
              >
                <Heart className="w-6 h-6" />
                <div className="text-left">
                  <div className="font-semibold text-lg">Prayer Focus</div>
                  <div className="text-sm opacity-90">5 prayer points</div>
                </div>
              </button>
            </div>
          </div>

          {/* Score Summary */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* Your Score */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="text-sm text-gray-500 mb-2">Your Score</div>
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-4xl font-bold text-gray-900">{userScore}</div>
                  <div className="text-gray-500">out of 50</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.round((userScore / 50) * 100)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Partner's Score */}
            {partnerScore > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="text-sm text-gray-500 mb-2">{partnerName}'s Score</div>
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-4xl font-bold text-gray-900">{partnerScore}</div>
                    <div className="text-gray-500">out of 50</div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">
                      {Math.round((partnerScore / 50) * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Interpretation */}
          {interpretation && (
            <div className={`rounded-xl border-2 p-6 mb-6 ${interpretationColor}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="text-3xl">
                  {interpretation.color === 'green' ? '✅' : interpretation.color === 'yellow' ? '⚠️' : '🔴'}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{interpretation.level}</h3>
                  <p className="text-sm opacity-90">{userScore} out of 50 points</p>
                </div>
              </div>
              <p className="font-medium mb-4">{interpretation.message}</p>
              
              {/* Pastoral Guidance */}
              <div className="bg-white bg-opacity-50 rounded-lg p-4 mt-4">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  🙏 Pastoral Guidance
                </h4>
                <p className="text-sm">{interpretation.pastoralGuidance}</p>
              </div>
            </div>
          )}

          {/* Question-by-Question Review */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Question-by-Question Review
            </h3>

            <div className="space-y-4">
              {factorData.questions?.map((question, index) => {
                const questionId = question.id;
                const userAnswer = userAnswers[questionId];
                const answerPoints = userAnswer || 0;

                return (
                  <div key={questionId} className="border border-gray-200 rounded-lg p-4">
                    {/* Question Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-500 mb-1">
                          Question {index + 1}
                        </div>
                        <p className="text-gray-900 font-medium">{question.text}</p>
                      </div>
                      <div className="ml-4 text-right">
                        <div className="text-2xl font-bold text-gray-900">{answerPoints}</div>
                        <div className="text-xs text-gray-500">/ 5 pts</div>
                      </div>
                    </div>

                    {/* Your Answer */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                      <div className="text-xs text-blue-600 font-medium mb-1">Your Answer:</div>
                      <div className="text-sm text-blue-900 font-medium">
                        {getAnswerText(question, userAnswer)}
                      </div>
                    </div>

                    {/* Insight */}
                    {question.insight && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <div className="text-xs text-purple-600 font-medium mb-1">
                          💡 Insight:
                        </div>
                        <p className="text-sm text-purple-900">{question.insight}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Next Steps</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">1.</span>
                <span>Review the teaching slides and pray through the prayer points together</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">2.</span>
                <span>Discuss these results with your partner in a loving, open conversation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">3.</span>
                <span>Schedule a counseling session to review insights together</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">4.</span>
                <span>Work on areas identified for growth with your counselor's guidance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">5.</span>
                <span>Continue to the next factor when your counselor unlocks it</span>
              </li>
            </ul>
          </div>

          {/* Back Button */}
          <div className="mt-6">
            <button
              onClick={() => navigate('/couple/dashboard')}
              className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors text-lg"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FactorResultsPage;